// src/app/audit/AI/CoPilotV2/ChatPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import styled from "styled-components";
import {
  SG_GET_CHAT_ROOM,
  SG_CREATE_CHAT_ROOM,
  SG_FETCH_CHAT_ROOM_V2,
  SG_DELETE_CHAT_ROOM,
  DISCONNECT_WEBSOCKET,
  SAGA_CONNECT_WEBSOCKET,
  MESSAGE_WEBSOCKET,
  SAGA_MESSAGE_WEBSOCKET,
  CLEAR_CHAT_MESSAGES,
  CLEAR_CREATED_ROOM,
  CHAT_ROOM_PAGE,
} from "constants/actions";

// Components
import ChatRoom from "./components/ChatRoom";
import BasicErrorMessage from "utilities/ErrorMessages";
import NewChatModal from "./components/NewChatModal";
import ChatRoomList from "./components/ChatRoomList";
import Error from "./components/Error";

const ChatPage = () => {
  const dispatch = useDispatch();
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const [chatUserName, setChatUserName] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingInitialMessage, setPendingInitialMessage] = useState(null);
  // Add new state for tracking initial message

  // Redux state
  // Add selector for newly created room
  const createdRoom = useSelector((state) => state.ai_chatv2?.created, shallowEqual);

  const updatedRoom = useSelector(
    (state) => state.ai_chatv2?.updated,
    shallowEqual
  );
  // const isLoadingList = useSelector((state) => state.ai_chat?.isLoadingList);
  // const isLoadingRoom = useSelector((state) => state.ai_chat?.isLoadingRoom);

  const chatToken = useSelector(
    (state) => state.auth?.chat_token,
    shallowEqual
  );
  const usrEmployee = useSelector(
    (state) => state.employees?.userEmp,
    shallowEqual
  );

  const chatRooms = useSelector(
    (state) => state.ai_chatv2?.chat_topics || [],
    shallowEqual
  );
  const currentRoom = useSelector(
    (state) => state.ai_chatv2?.chatData,
    shallowEqual
  );
  const wsConnected = useSelector(
    (state) => state.ai_websocketv2?.isConnected,
    shallowEqual
  );
  const wsError = useSelector(
    (state) => state.ai_websocketv2?.error,
    shallowEqual
  );

  useEffect(() => {
    setChatUserName(usrEmployee?.first_name + " " + usrEmployee?.last_name);
  }, [usrEmployee]);

  // Modified useEffect for handling room creation
  useEffect(() => {
    if (createdRoom?.id) {
      // First update the room list
      dispatch({ type: SG_GET_CHAT_ROOM, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [createdRoom, dispatch]);

  useEffect(() => {
    if (updatedRoom) {
      dispatch({ type: CLEAR_CREATED_ROOM });
      dispatch({ type: SG_GET_CHAT_ROOM, payload: `page=1` });
      dispatch({ type: CHAT_ROOM_PAGE, payload: 1 });
      setCurrentPage(1);
    }
  }, [dispatch, updatedRoom]);

  useEffect(() => {
    dispatch({ type: SG_GET_CHAT_ROOM, payload: `page=${currentPage}` });
  }, [dispatch, currentPage]);

  // Step 1: Handle new chat creation and room setup
  const handleStartNewChat = (payload) => {
    dispatch({ type: CLEAR_CHAT_MESSAGES }); // Clear message history
    console.log("Starting new chat with payload:", payload);

    const initialMessage = {
      id: `msg-${Date.now()}`,
      message: payload.initial_message.message,
      from: "user",
      username: chatUserName,
      timestamp: new Date().toISOString(),
    };

    setPendingInitialMessage(initialMessage);

    dispatch({
      type: SG_CREATE_CHAT_ROOM,
      payload: payload,
    });
  };

  // Step 2: Handle room creation and WebSocket connection
  useEffect(() => {
    if (!createdRoom?.id || !pendingInitialMessage) return;

    const roomId = createdRoom.id;
    // First disconnect any existing connection
    if (selectedRoomId) {
      dispatch({
        type: DISCONNECT_WEBSOCKET,
        payload: { web_chat_id: selectedRoomId },
      });
    }
    // Set new room ID
    setSelectedRoomId(roomId);
    // Connect WebSocket
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET,
      payload: { web_chat_id: roomId },
    });
    // Fetch room details
    dispatch({
      type: SG_FETCH_CHAT_ROOM_V2,
      payload: { id: roomId },
    });
  }, [createdRoom, pendingInitialMessage, selectedRoomId]);

  // Step 3: Send initial message after connection
  useEffect(() => {
    if (wsConnected && pendingInitialMessage && selectedRoomId) {
      // console.log(
      //   "WebSocket connected, waiting before sending initial message..."
      // );

      // Add delay before sending
      const timer = setTimeout(() => {
        // console.log("Attempting to send initial message");

        const messagePayload = {
          id: `msg-${Date.now()}`,
          message: pendingInitialMessage.message.trim(),
          web_chat_id: selectedRoomId,
          from: "user",
          username: chatUserName,
          timestamp: new Date().toISOString(),
          type: "user",
        };

        // First dispatch the message to local state
        dispatch({
          type: MESSAGE_WEBSOCKET,
          payload: messagePayload,
        });

        // Then send through websocket
        dispatch({
          type: SAGA_MESSAGE_WEBSOCKET,
          payload: messagePayload,
        });

        // Cleanup
        dispatch({ type: CLEAR_CREATED_ROOM });
        setPendingInitialMessage(null);
      }, 1000); // 1 second delay

      // Cleanup timer if component unmounts or dependencies change
      return () => clearTimeout(timer);
    }
  }, [wsConnected, pendingInitialMessage, selectedRoomId, chatUserName]);

  // Regular Room Selection Handler
  const handleRoomSelect = (roomId) => {
    if (selectedRoomId) {
      dispatch({
        type: DISCONNECT_WEBSOCKET,
        payload: { web_chat_id: selectedRoomId },
      });
    }

    // Set new room ID
    setSelectedRoomId(roomId);

    // Connect WebSocket for existing room
    dispatch({
      type: SAGA_CONNECT_WEBSOCKET,
      payload: { web_chat_id: roomId },
    });

    // Fetch room history
    dispatch({
      type: SG_FETCH_CHAT_ROOM_V2,
      payload: { id: roomId },
    });
  };

  const handleDeleteRoom = (roomId) => {
    dispatch({
      type: SG_DELETE_CHAT_ROOM,
      payload: { id: roomId },
    });
    if (selectedRoomId === roomId) {
      setSelectedRoomId(null);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <PageContainer>
      <SidebarContainer>
        <SidebarHeader>
          <h2>Chat History</h2>
          <NewChatButton onClick={() => setIsNewChatModalOpen(true)}>
            + New Chat
          </NewChatButton>
        </SidebarHeader>
        <ChatRoomList
          rooms={chatRooms}
          selectedId={selectedRoomId}
          onSelect={handleRoomSelect}
          onDelete={handleDeleteRoom}
          onPageChange={handlePageChange}
        />
      </SidebarContainer>
      <MainContent>
        {wsError && <Error message={wsError} />}
        <BasicErrorMessage />
        {selectedRoomId ? (
          <ChatRoom
            web_chat_id={selectedRoomId}
            chatUserName={chatUserName}
            messages={currentRoom?.interaction_history || []}
            wsConnected={wsConnected}
          />
        ) : (
          <WelcomePlaceholder>
            <h2>Welcome to Chat</h2>
            <p>Select a chat from the sidebar or start a new conversation</p>
          </WelcomePlaceholder>
        )}
      </MainContent>

      <NewChatModal
        isOpen={isNewChatModalOpen}
        chatToken={chatToken}
        chatUserName={chatUserName}
        onClose={() => setIsNewChatModalOpen(false)}
        onSubmit={handleStartNewChat}
      />
    </PageContainer>
  );
};

export default ChatPage;

// Add new styled component for welcome message
const WelcomePlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  color: #6c757d;

  h2 {
    margin-bottom: 12px;
    font-size: 1.5rem;
  }

  p {
    font-size: 1rem;
  }
`;
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  background: #f8f9fa;
`;

const SidebarContainer = styled.div`
  width: 300px;
  background: white;
  border-right: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NewChatButton = styled.button`
  padding: 8px 16px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
